
import React from "react";
import { Users, X, PlusCircle, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

interface TeamConfigurationProps {
  teamName: string;
  setTeamName: (name: string) => void;
  selectedSkills: string[];
  setSelectedSkills: (skills: string[]) => void;
  projectType: string;
  setProjectType: (type: string) => void;
  teamSize: string;
  setTeamSize: (size: string) => void;
  selectedFreelancers: any[];
  saveTeam: () => void;
}

export const TeamConfiguration: React.FC<TeamConfigurationProps> = ({
  teamName,
  setTeamName,
  selectedSkills,
  setSelectedSkills,
  projectType,
  setProjectType,
  teamSize,
  setTeamSize,
  selectedFreelancers,
  saveTeam
}) => {
  // Function to add a skill to selectedSkills
  const addSkill = (skill: string) => {
    if (!selectedSkills.includes(skill) && skill !== "all-skills") {
      setSelectedSkills([...selectedSkills, skill]);
    } else if (skill === "all-skills") {
      setSelectedSkills(["all-skills"]);
    }
  };

  // Function to remove a skill from selectedSkills
  const removeSkill = (skill: string) => {
    setSelectedSkills(selectedSkills.filter(s => s !== skill));
  };

  // Available skills list
  const availableSkills = [
    { value: "all-skills", label: "All Skills" },
    { value: "React", label: "React" },
    { value: "Node.js", label: "Node.js" },
    { value: "UI/UX", label: "UI/UX Design" },
    { value: "Python", label: "Python" },
    { value: "AWS", label: "AWS" },
    { value: "DevOps", label: "DevOps" },
    { value: "Mobile", label: "Mobile Development" },
    { value: "Data", label: "Data Science" }
  ];

  return (
    <Card className="lg:col-span-1 h-fit sticky top-4 border-t-4 border-t-indigo-500 shadow-md">
      <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-t-lg">
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-indigo-600" />
          Team Configuration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 pt-5">
        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center">
            Team Name
            {teamName.trim() && <CheckCircle className="ml-2 h-4 w-4 text-green-500" />}
          </label>
          <Input 
            placeholder="Enter team name" 
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            className="border-indigo-200 focus:border-indigo-400 transition-all duration-200"
          />
          {!teamName.trim() && (
            <p className="text-xs text-indigo-600 italic mt-1">
              A team name is required to save your team
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Required Skills</label>
          <Select 
            value={""} 
            onValueChange={addSkill}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select skills" />
            </SelectTrigger>
            <SelectContent>
              {availableSkills.map(skill => (
                <SelectItem key={skill.value} value={skill.value}>
                  {skill.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Display selected skills as badges */}
          {selectedSkills.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedSkills.map(skill => (
                <Badge 
                  key={skill} 
                  className="bg-indigo-100 text-indigo-800 hover:bg-indigo-200 transition-colors"
                >
                  {skill === "all-skills" ? "All Skills" : skill}
                  <X 
                    className="h-3.5 w-3.5 ml-1 cursor-pointer" 
                    onClick={() => removeSkill(skill)}
                  />
                </Badge>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Project Type</label>
          <Select value={projectType} onValueChange={setProjectType}>
            <SelectTrigger className="border-indigo-200 focus:border-indigo-400 transition-all duration-200">
              <SelectValue placeholder="Select project type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              <SelectItem value="web">Web Development</SelectItem>
              <SelectItem value="mobile">Mobile App</SelectItem>
              <SelectItem value="design">Design</SelectItem>
              <SelectItem value="marketing">Marketing</SelectItem>
              <SelectItem value="data">Data Analytics</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Team Size</label>
          <Select value={teamSize} onValueChange={setTeamSize}>
            <SelectTrigger className="border-indigo-200 focus:border-indigo-400 transition-all duration-200">
              <SelectValue placeholder="Select team size" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any Size</SelectItem>
              <SelectItem value="small">Small (2-3)</SelectItem>
              <SelectItem value="medium">Medium (4-5)</SelectItem>
              <SelectItem value="large">Large (6+)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent>
      <CardFooter className="flex-col space-y-4 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-b-lg p-6">
        <div className="w-full">
          <div className="flex justify-between text-sm mb-1">
            <span className="font-medium">Team Completeness</span>
            <span className="font-bold text-indigo-700">{selectedFreelancers.length > 0 ? Math.min(100, selectedFreelancers.length * 20) : 0}%</span>
          </div>
          <Progress 
            value={selectedFreelancers.length > 0 ? Math.min(100, selectedFreelancers.length * 20) : 0} 
            className="h-2.5 bg-gray-200" 
            indicatorClassName={`bg-gradient-to-r ${
              selectedFreelancers.length >= 3 ? 'from-indigo-500 to-green-500' : 'from-indigo-400 to-blue-500'
            }`}
          />
          <div className="text-xs text-gray-600 mt-1 flex justify-between">
            <span>0 members</span>
            <span>5 members</span>
          </div>
        </div>
        <Button 
          onClick={saveTeam} 
          className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 transition-all duration-300"
          disabled={selectedFreelancers.length === 0 || !teamName.trim()}
        >
          <PlusCircle className="h-4 w-4 mr-2" />
          Save Team
        </Button>
        {selectedFreelancers.length === 0 && (
          <p className="text-xs text-center text-indigo-600 italic">
            Add at least one member to enable saving
          </p>
        )}
      </CardFooter>
    </Card>
  );
};
